import { animated } from "@react-spring/web";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useSlides from "@app/common/hooks/useSlides";
import ROUTES from "@app/common/routes";

import Button from "@rui/Button";
import { Link } from "@rui/Link";
import Modal, { ModalProps } from "@rui/Modal";
import Stack, { Row } from "@rui/Stack";
import Stepper from "@rui/Stepper";

import useBillingContext from "../hooks/billing/useBillingContext";

const AnimatedStack = animated(Stack);

const StyledButton = styled(Button)({
  minWidth: "130px",
});

const StyledModal = styled(Modal)({
  "& .MuiDialog-paper": {
    overflow: "hidden",
    maxWidth: "410px",
    minWidth: "0px",
    "& .MuiPaper-root": {
      minWidth: "0px",
      width: "410px",
    },
  },
});

const ContentContainer = styled(Stack)({
  height: "180px",
});

const YourPlan = () => {
  const { currentPlan, subscription } = useBillingContext();

  const noCredits = subscription.settings.monthlyFreeUploadCredits === 0;

  return (
    <Stack spacing={1.5}>
      <Typography variant="bodyPrimaryReg">
        Upload video files from your device to generate Read Meeting Reports.
        Each minute of video requires 1 credit.
      </Typography>
      <Typography variant="bodyPrimaryReg">
        {currentPlan.label ? (
          <>
            You are on the {currentPlan.label}{" "}
            {subscription.inTrialPeriod ? "trial" : "plan"},{" "}
            {noCredits ? (
              <>
                which does not include free credits per month. You can purchase
                credits any time on the{" "}
                <Link to={ROUTES.BILLING}>billing page</Link>.
              </>
            ) : (
              `which includes
        ${subscription.settings.monthlyFreeUploadCredits} free minutes each
        month! Additional credits can be purchased any time.`
            )}
          </>
        ) : (
          <>
            You can purchase credits any time on the{" "}
            <Link to={ROUTES.BILLING}>billing page</Link>.
          </>
        )}
      </Typography>
    </Stack>
  );
};

const FinalStep = () => (
  <Stack spacing={1.5}>
    <Typography variant="bodyPrimaryReg">
      Once your video is uploaded, it will be processed and converted into a
      report.
    </Typography>
    <Typography variant="bodyPrimaryReg">
      This process can take as long as 24 hours, depending on the size of the
      upload.
    </Typography>
    <Typography variant="bodyPrimaryReg">
      Get started by selecting a file to upload!
    </Typography>
  </Stack>
);

const SLIDES = [YourPlan, FinalStep];

const UploadTutorial = (props: ModalProps & { onClose: () => void }) => {
  const { activeSlideIndex, nextSlide, jumpToSlide, showNext, transition } =
    useSlides({
      slides: SLIDES,
    });

  const ActiveSlideContent = SLIDES[activeSlideIndex];

  const handleClose = () => {
    props.onClose();
    jumpToSlide(0);
  };

  if (!props.open) return null;

  return (
    <StyledModal {...props} onClose={handleClose}>
      <Typography variant="h2">Uploading videos to Read</Typography>
      <ContentContainer>
        {transition((style) => (
          <AnimatedStack
            style={{
              ...style,
              position: "absolute",
              top: "60px",
              width: "90%",
            }}
          >
            <ActiveSlideContent />
          </AnimatedStack>
        ))}
      </ContentContainer>
      <Row variant="apart">
        <Stepper
          activeStep={activeSlideIndex}
          onClickStep={jumpToSlide}
          steps={SLIDES.length}
          activeStepColor="shades.secondary.600"
          inactiveStepColor="shades.neutral.100"
        />
        {showNext ? (
          <StyledButton onClick={nextSlide}>Next</StyledButton>
        ) : (
          <StyledButton onClick={handleClose}>Got it</StyledButton>
        )}
      </Row>
    </StyledModal>
  );
};

export default UploadTutorial;
